import { LegalPageLayout } from "@/components/legal/LegalPageLayout";
import { LegalSection } from "@/components/legal/LegalSection";

const tableOfContents = [
  { id: "acceptance", title: "Acceptance of Terms", level: 1 },
  { id: "description", title: "Description of Service", level: 1 },
  {
    id: "emergency-services",
    title: "Emergency Services and Location Features",
    level: 1,
  },
  { id: "eligibility", title: "Eligibility and Registration", level: 1 },
  { id: "user-conduct", title: "User Conduct and Responsibilities", level: 1 },
  { id: "content-policy", title: "Content and Community Guidelines", level: 1 },
  {
    id: "intellectual-property",
    title: "Intellectual Property Rights",
    level: 1,
  },
  { id: "privacy", title: "Privacy and Data Protection", level: 1 },
  { id: "prohibited-activities", title: "Prohibited Activities", level: 1 },
  { id: "termination", title: "Account Termination", level: 1 },
  { id: "disclaimers", title: "Disclaimers and Limitations", level: 1 },
  { id: "indemnification", title: "Indemnification", level: 1 },
  { id: "governing-law", title: "Governing Law and Disputes", level: 1 },
  { id: "changes", title: "Changes to Terms", level: 1 },
  { id: "contact", title: "Contact Information", level: 1 },
];

export default function TermsOfServicePage() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated="January 6, 2025"
      language="en"
      alternateUrl="/pages/vi/terms"
      tableOfContents={tableOfContents}
    >
      <LegalSection id="acceptance" title="Acceptance of Terms">
        <p>
          Welcome to Locuno! These Terms of Service ("Terms") constitute a
          legally binding agreement between you and Cloudjet Solutions Pte. Ltd.
          ("Locuno," "we," "us," or "our") governing your use of the Locuno
          social networking platform, including our website, mobile
          applications, and all related services (collectively, the "Services").
        </p>
        <p>
          By accessing, browsing, or using our Services, you acknowledge that
          you have read, understood, and agree to be bound by these Terms and
          our{" "}
          <a href="/pages/privacy" className="text-blue-600 hover:underline">
            Privacy Policy
          </a>
          . If you do not agree to these Terms, you must not use our Services.
        </p>
        <p>
          These Terms apply to all users of our Services, including visitors,
          registered users, content creators, and business accounts.
        </p>
      </LegalSection>

      <LegalSection id="description" title="Description of Service">
        <p>
          Locuno is a social networking platform that connects people within
          local communities. Our Services enable users to:
        </p>
        <ul>
          <li>
            Create profiles and connect with friends, neighbors, and local
            community members
          </li>
          <li>
            Share content including text posts, photos, videos, and
            location-based updates
          </li>
          <li>
            Discover and participate in local events, groups, and activities
          </li>
          <li>
            Find and recommend local businesses, services, and places of
            interest
          </li>
          <li>Buy and sell items in the local marketplace</li>
          <li>
            Communicate through messaging, comments, and group discussions
          </li>
          <li>Access location-based features and community-specific content</li>
        </ul>
        <p>
          We reserve the right to modify, suspend, or discontinue any part of
          our Services at any time, with or without notice. We may also impose
          limits on certain features or restrict access to parts of the Services
          without notice or liability.
        </p>
      </LegalSection>

      <LegalSection
        id="emergency-services"
        title="Emergency Services and Location Features"
      >
        <h3>EULA Disclaimer on Emergency Services and Location-Based Alerts</h3>
        <p>
          <strong>
            THIS SERVICE IS NOT A REPLACEMENT FOR 911 OR PUBLIC EMERGENCY
            SERVICES.
          </strong>
        </p>
        <p>
          Locuno is a personal safety network tool. The "SOS" feature is
          designed to alert and connect you with your own pre-selected trusted
          contacts ("Responders," e.g., family, friends, neighbors) via Voice
          over IP (VoIP), video, and real-time location sharing.
        </p>
        <p>You, the user, acknowledge and agree to the following:</p>

        <h4>No Connection to Public Services</h4>
        <p>
          The app does not connect, dispatch, or communicate with any public
          emergency services, first responders, or public safety answering
          points (PSAPs), such as 911, 999, or 112. Activating the SOS feature
          will not result in police, fire, or medical services being
          automatically dispatched.
        </p>

        <h4>User Responsibility</h4>
        <p>
          In the event of a critical emergency, you should contact public
          emergency services directly through traditional means (e.g., by
          dialing 911). You should not rely on Locuno as your primary method for
          contacting emergency help in life-threatening situations.
        </p>

        <h4>Dependent on Personal Network</h4>
        <p>
          The effectiveness of the SOS feature is entirely dependent on your
          chosen Responders being available, willing, and able to receive the
          alert and provide assistance. We do not guarantee that your Responders
          will receive the alert or respond.
        </p>

        <h4>Location Sharing</h4>
        <p>
          By activating the SOS feature, you consent to your real-time location
          data being shared with your designated Responders for the duration of
          the alert. The purpose of this is to allow your personal network to
          identify your location and provide assistance. Locuno does not and
          cannot verify the location information received by your Responders.
        </p>

        <h3>How the SOS Feature Works</h3>
        <p>
          When you enable location services and activate the SOS feature in our
          app:
        </p>
        <ul>
          <li>
            <strong>VoIP/Video Call:</strong> The app initiates a Voice over IP
            (VoIP) call or video call with your pre-selected trusted contacts
            (Responders)
          </li>
          <li>
            <strong>Location Sharing:</strong> Your real-time location is shared
            only with your designated Responders within the Locuno network
          </li>
          <li>
            <strong>Personal Network Alert:</strong> Your trusted contacts
            receive an in-app notification and can view your location to provide
            assistance
          </li>
          <li>
            <strong>Peer-to-Peer Communication:</strong> All communication
            happens directly between you and your personal network, not through
            any public emergency dispatch system
          </li>
        </ul>

        <h3>Service Limitations</h3>
        <p>
          <strong>CRITICAL LIMITATIONS:</strong>
        </p>
        <ul>
          <li>
            <strong>Not a Replacement for 911:</strong> The SOS feature is NOT a
            substitute for calling official emergency services (911, 112, etc.)
          </li>
          <li>
            <strong>No Public Emergency Connection:</strong> We do not connect
            to, integrate with, or transmit data to any public emergency
            services, first responders, or PSAPs
          </li>
          <li>
            <strong>Depends on Your Network:</strong> The effectiveness depends
            entirely on your Responders being available, willing, and able to
            help
          </li>
          <li>
            <strong>Technical Limitations:</strong> Service availability depends
            on internet connectivity, GPS signal, device functionality, and your
            Responders having the app installed and active
          </li>
          <li>
            <strong>User Responsibility:</strong> You remain solely responsible
            for contacting public emergency services (911, 112, etc.) directly
            for life-threatening situations
          </li>
          <li>
            <strong>No Response Guarantee:</strong> We cannot guarantee that
            your Responders will receive the alert, respond, or provide
            assistance
          </li>
        </ul>

        <h3>User Acknowledgment</h3>
        <p>
          By using Locuno's SOS and location features, you acknowledge and agree
          that:
        </p>
        <ul>
          <li>
            You understand this is a peer-to-peer personal safety network, not
            an emergency response service
          </li>
          <li>
            You will always contact official emergency services (911, 112, etc.)
            directly for life-threatening situations
          </li>
          <li>
            You consent to sharing your location data with your designated
            Responders within the Locuno network
          </li>
          <li>
            You understand the app does not connect to any public emergency
            services or dispatch systems
          </li>
          <li>You will not rely solely on Locuno for emergency assistance</li>
          <li>
            Your Responders may choose to contact public emergency services on
            your behalf if they determine it is necessary
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="eligibility" title="Eligibility and Registration">
        <h3>Age Requirements</h3>
        <p>
          You must be at least 13 years old to use our Services. If you are
          under 18, you represent that you have obtained parental or guardian
          consent to use our Services.
        </p>

        <h3>Account Registration</h3>
        <p>
          To use certain features of our Services, you must register for an
          account. When registering, you agree to:
        </p>
        <ul>
          <li>Provide accurate, current, and complete information</li>
          <li>Maintain and promptly update your account information</li>
          <li>Keep your password secure and confidential</li>
          <li>Accept responsibility for all activities under your account</li>
          <li>
            Notify us immediately of any unauthorized access or security breach
          </li>
        </ul>

        <h3>One Account Per Person</h3>
        <p>
          You may only create one account per person. Creating multiple accounts
          or impersonating others is prohibited and may result in account
          termination.
        </p>

        <h3>Geographic Restrictions</h3>
        <p>
          Our Services are intended for users in regions where they are legally
          available. You are responsible for complying with local laws and
          regulations in your jurisdiction.
        </p>
      </LegalSection>

      <LegalSection id="user-conduct" title="User Conduct and Responsibilities">
        <p>As a user of our Services, you agree to:</p>
        <ul>
          <li>
            Use the Services in compliance with all applicable laws and
            regulations
          </li>
          <li>Respect the rights and dignity of other users</li>
          <li>
            Provide accurate information and not misrepresent your identity
          </li>
          <li>
            Maintain the security and confidentiality of your account
            credentials
          </li>
          <li>Report violations of these Terms or illegal activities to us</li>
          <li>Respect intellectual property rights of others</li>
          <li>Use the Services for their intended purpose only</li>
        </ul>

        <h3>Community Standards</h3>
        <p>
          Locuno is built on the principle of creating safe, welcoming local
          communities. We expect all users to contribute positively to this
          environment by treating others with respect and following our
          community guidelines.
        </p>

        <h3>User-Generated Content</h3>
        <p>
          You are solely responsible for any content you post, share, or
          transmit through our Services. This includes ensuring you have all
          necessary rights and permissions for content you share.
        </p>
      </LegalSection>

      <LegalSection
        id="content-policy"
        title="Content and Community Guidelines"
      >
        <h3>Acceptable Content</h3>
        <p>When using our Services, you may post content that is:</p>
        <ul>
          <li>Respectful and constructive</li>
          <li>Relevant to local communities and interests</li>
          <li>Original or properly attributed</li>
          <li>Compliant with applicable laws</li>
          <li>Appropriate for all ages (when in public areas)</li>
        </ul>

        <h3>Prohibited Content</h3>
        <p>You may not post, share, or transmit content that:</p>
        <ul>
          <li>
            Is illegal, harmful, threatening, or promotes illegal activities
          </li>
          <li>Contains hate speech, discrimination, or harassment</li>
          <li>Is sexually explicit, pornographic, or inappropriate</li>
          <li>Violates intellectual property rights</li>
          <li>Contains false, misleading, or deceptive information</li>
          <li>Promotes violence, self-harm, or dangerous activities</li>
          <li>Includes spam, malware, or phishing attempts</li>
          <li>
            Violates privacy rights or contains unauthorized personal
            information
          </li>
          <li>Is intended to manipulate or deceive other users</li>
        </ul>

        <h3>Content Moderation</h3>
        <p>
          We reserve the right to review, moderate, edit, or remove any content
          that violates these Terms or our community guidelines. We may also
          restrict or terminate accounts that repeatedly violate our policies.
        </p>

        <h3>Reporting Violations</h3>
        <p>
          Users can report content or behavior that violates our Terms through
          our reporting mechanisms. We investigate all reports and take
          appropriate action when violations are confirmed.
        </p>
      </LegalSection>

      <LegalSection
        id="intellectual-property"
        title="Intellectual Property Rights"
      >
        <h3>Our Intellectual Property</h3>
        <p>
          The Locuno Services, including our software, technology, content,
          trademarks, logos, and design, are owned by Cloudjet Solutions Pte.
          Ltd. and are protected by intellectual property laws. You may not
          copy, modify, distribute, or create derivative works from our
          proprietary content.
        </p>

        <h3>User Content License</h3>
        <p>
          When you post content to our Services, you grant Locuno a
          non-exclusive, royalty-free, worldwide license to use, display,
          reproduce, modify, and distribute your content for the purpose of
          providing and improving our Services. This license continues until you
          delete the content or close your account.
        </p>

        <h3>Respecting Others' Rights</h3>
        <p>
          You must respect the intellectual property rights of others. Do not
          post content that infringes on copyrights, trademarks, or other
          proprietary rights without proper authorization.
        </p>

        <h3>DMCA Compliance</h3>
        <p>
          We comply with the Digital Millennium Copyright Act (DMCA) and respond
          to valid takedown notices. If you believe your copyright has been
          infringed, please contact us with the required information as
          specified in our DMCA policy.
        </p>
      </LegalSection>

      <LegalSection id="privacy" title="Privacy and Data Protection">
        <p>
          Your privacy is important to us. Our collection, use, and protection
          of your personal information is governed by our{" "}
          <a href="/pages/privacy" className="text-blue-600 hover:underline">
            Privacy Policy
          </a>
          , which is incorporated into these Terms by reference.
        </p>
        <p>Key privacy principles include:</p>
        <ul>
          <li>Transparency about data collection and use</li>
          <li>User control over privacy settings and data sharing</li>
          <li>Protection of personal information through security measures</li>
          <li>Compliance with applicable privacy laws and regulations</li>
          <li>Respect for user choices regarding data processing</li>
        </ul>
        <p>
          By using our Services, you consent to the collection and use of your
          information as described in our Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection id="prohibited-activities" title="Prohibited Activities">
        <p>
          You are prohibited from engaging in any of the following activities:
        </p>

        <h3>Technical Violations</h3>
        <ul>
          <li>
            Attempting to gain unauthorized access to our systems or other
            users' accounts
          </li>
          <li>
            Using automated tools, bots, or scripts to access or interact with
            our Services
          </li>
          <li>
            Reverse engineering, decompiling, or attempting to extract source
            code
          </li>
          <li>Interfering with or disrupting the operation of our Services</li>
          <li>Transmitting viruses, malware, or other harmful code</li>
        </ul>

        <h3>Misuse and Abuse</h3>
        <ul>
          <li>Creating fake accounts or impersonating others</li>
          <li>Engaging in harassment, bullying, or threatening behavior</li>
          <li>Spamming users with unsolicited messages or content</li>
          <li>
            Using our Services for commercial purposes without authorization
          </li>
          <li>Collecting user information for unauthorized purposes</li>
        </ul>

        <h3>Illegal Activities</h3>
        <ul>
          <li>
            Using our Services to facilitate illegal transactions or activities
          </li>
          <li>Posting content that violates applicable laws or regulations</li>
          <li>Infringing on intellectual property rights</li>
          <li>Violating privacy rights or applicable data protection laws</li>
        </ul>
      </LegalSection>

      <LegalSection id="termination" title="Account Termination">
        <h3>Termination by You</h3>
        <p>
          You may terminate your account at any time by following the account
          deletion process in your account settings. Upon termination, your
          access to the Services will be disabled, and your account data will be
          handled according to our Privacy Policy.
        </p>

        <h3>Termination by Us</h3>
        <p>We may suspend or terminate your account if you:</p>
        <ul>
          <li>Violate these Terms or our community guidelines</li>
          <li>Engage in illegal or harmful activities</li>
          <li>
            Create multiple accounts or attempt to circumvent restrictions
          </li>
          <li>
            Fail to respond to requests for verification or additional
            information
          </li>
          <li>Remain inactive for an extended period</li>
        </ul>

        <h3>Effect of Termination</h3>
        <p>
          Upon termination of your account, your right to use the Services will
          cease immediately. We may retain certain information as required by
          law or for legitimate business purposes. Provisions of these Terms
          that by their nature should survive termination will remain in effect.
        </p>
      </LegalSection>

      <LegalSection id="disclaimers" title="Disclaimers and Limitations">
        <p>
          Our Services are provided "as is" and "as available." We do not
          guarantee uninterrupted, error-free service. We are not liable for
          user-generated content or third-party services.
        </p>

        <h3>Personal Safety Network Disclaimer</h3>
        <p>
          <strong>CRITICAL DISCLAIMER:</strong> Cloudjet Solutions Pte. Ltd.
          expressly disclaims any and all liability for emergency situations or
          any outcomes related to use of the SOS feature. Our SOS and
          location-based features are provided as a peer-to-peer personal safety
          network tool only and are NOT intended as, and do not function as,
          emergency response services.
        </p>
        <ul>
          <li>
            <strong>Peer-to-Peer Network Only:</strong> The SOS feature only
            connects you with your pre-selected personal contacts (Responders)
            within the Locuno network. We do not connect to, integrate with, or
            transmit data to any public emergency services
          </li>
          <li>
            <strong>No Response Guarantee:</strong> We do not guarantee that
            your Responders will receive alerts, respond, or provide assistance
          </li>
          <li>
            <strong>User Responsibility:</strong> Users remain solely
            responsible for contacting public emergency services (911, 112,
            etc.) directly for life-threatening situations
          </li>
          <li>
            <strong>Technical Limitations:</strong> The SOS feature may fail due
            to technical issues, network problems, device limitations, or if
            your Responders do not have the app installed or active
          </li>
        </ul>

        <h3>Location Services Disclaimer</h3>
        <p>
          Location-based features are provided to help your personal network
          (Responders) identify your location. We do not guarantee:
        </p>
        <ul>
          <li>Accuracy of location data</li>
          <li>Availability of location services</li>
          <li>
            Successful transmission of location information to your Responders
          </li>
          <li>
            That your Responders will receive or be able to view the location
            data
          </li>
        </ul>

        <h3>Limitation of Liability</h3>
        <p>
          To the maximum extent permitted by law, Cloudjet Solutions Pte. Ltd.
          shall not be liable for any indirect, incidental, special,
          consequential, or punitive damages, including but not limited to
          damages arising from:
        </p>
        <ul>
          <li>
            Emergency situations or any outcomes related to use of the SOS
            feature
          </li>
          <li>Location service failures or inaccuracies</li>
          <li>Actions or inactions of your designated Responders</li>
          <li>User reliance on the peer-to-peer SOS feature</li>
          <li>Any personal injury or property damage</li>
        </ul>
      </LegalSection>

      <LegalSection id="indemnification" title="Indemnification">
        <p>
          You agree to indemnify and hold harmless Cloudjet Solutions Pte. Ltd.
          from claims arising from your use of our Services, violations of these
          Terms, or actions that infringe upon others' rights.
        </p>
      </LegalSection>

      <LegalSection id="governing-law" title="Governing Law and Disputes">
        <p>
          These Terms are governed by Vietnamese law. Disputes will be resolved
          in Ho Chi Minh City courts or through Vietnam International
          Arbitration Centre (VIAC). Claims must be filed within one year and
          will be conducted on an individual basis only.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="Changes to Terms">
        <p>
          We may modify these Terms at any time. Material changes will be
          communicated through email, in-app notifications, or website notices.
          Continued use after changes indicates acceptance.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact Information">
        <p>Questions about these Terms? Contact us:</p>
        <div className="bg-gray-50 p-4 rounded-lg mt-4">
          <p>
            <strong>Company Name:</strong> CLOUDJET SOLUTIONS COMPANY LIMITED
          </p>
          <p>
            <strong>Address:</strong> 2nd Floor, F Central Building, 16A Le Hong
            Phong, Ward 12, District 10, Ho Chi Minh City, Vietnam
          </p>
          <p>
            <strong>Tax Code:</strong> 0312033924
          </p>
          <p>
            <strong>Phone:</strong> 028 628 00110
          </p>
          <p>
            <strong>Email:</strong> contact@locuno.com
          </p>
          <p>
            <strong>Legal Department:</strong> legal@locuno.com
          </p>
          <p>
            <strong>General Support:</strong> support@locuno.com
          </p>
          <p>
            <strong>Privacy:</strong> privacy@locuno.com
          </p>
        </div>
      </LegalSection>
    </LegalPageLayout>
  );
}
